import { createRoute, Link } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Badge } from "../components/ui/Badge/Badge";
import { Button } from "../components/ui/Button/Button";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { BackButton } from "../components/ui/BackButton/BackButton";
import {
	useCompanyDetail,
	useUpdateCompany,
	useSapStates,
	useSapCountries,
} from "../hooks/use-companies";
import { usePersonList, useCreatePerson } from "../hooks/use-persons";
import { useActivityList } from "../hooks/use-activities";
import { useOfferList } from "../hooks/use-offers";
import { formatDate, formatAmount } from "../lib/date";
import { isValidPhone } from "../lib/phone";
import styles from "./CompanyDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/companies/$id",
	component: withPermission(CompanyDetailPage, "companies", "read"),
});

function CompanyDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const companyQuery = useCompanyDetail(id);
	const personsQuery = usePersonList({ companyId: id });
	const activitiesQuery = useActivityList({ companyId: id });
	const offersQuery = useOfferList({ companyId: id });
	const updateCompany = useUpdateCompany();
	const { data: countries } = useSapCountries();
	const [editOpen, setEditOpen] = useState(false);
	const [editData, setEditData] = useState<Record<string, string>>({});
	const selectedCountryCode = countries?.find((c) => c.name === editData.addressCountry)?.code;
	const { data: states } = useSapStates(selectedCountryCode);
	const [editError, setEditError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [personOpen, setPersonOpen] = useState(false);
	const [personData, setPersonData] = useState<Record<string, string>>({});
	const [personError, setPersonError] = useState("");
	const createPerson = useCreatePerson();

	if (companyQuery.isLoading) return <LoadingSpinner />;
	if (companyQuery.isError || !companyQuery.data)
		return <ErrorFallback onRetry={companyQuery.refetch} />;

	const company = companyQuery.data;

	const openEdit = () => {
		setEditData({
			legalName: company.legalName,
			tradeName: company.tradeName ?? "",
			taxNumber: company.taxNumber ?? "",
			taxOffice: company.taxOffice ?? "",
			phone: company.phone ?? "",
			website: company.website ?? "",
			industry: company.industry ?? "",
			notes: company.notes ?? "",
			balance: company.balance ?? "",
			currency: company.currency ?? "",
			status: company.status,
			addressStreet: company.address?.street ?? "",
			addressCity: company.address?.city ?? "",
			addressState: company.address?.state ?? "",
			addressPostalCode: company.address?.postalCode ?? "",
			addressCountry: company.address?.country ?? "Türkiye",
		});
		setEditOpen(true);
	};

	const saveEdit = () => {
		setEditError("");
		const errors: Record<string, string> = {};
		if (!editData.legalName?.trim()) errors.legalName = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		if (editData.phone && !isValidPhone(editData.phone)) {
			setEditError(t("invalidPhone"));
			return;
		}
		const {
			addressStreet,
			addressCity,
			addressState,
			addressPostalCode,
			addressCountry,
			...companyData
		} = editData;
		updateCompany.mutate(
			{
				id,
				data: {
					...companyData,
					address: {
						street: addressStreet || undefined,
						city: addressCity || undefined,
						state: addressState || undefined,
						postalCode: addressPostalCode || undefined,
						country: addressCountry || undefined,
					},
				},
			},
			{
				onSuccess: () => {
					setEditOpen(false);
					alert(t("updateSuccess"));
				},
				onError: (err) => setEditError(err instanceof Error ? err.message : t("updateFailed")),
			},
		);
	};

	return (
		<div>
			<BackButton fallbackTo="/companies" />
			<PageHeader
				title={company.legalName}
				action={
					<div className={styles.actions}>
						<Link to="/offers/new" search={{ companyId: id }}>
							<Button variant="secondary" size="sm">
								{t("createOffer")}
							</Button>
						</Link>
						<Link to="/orders/new" search={{ companyId: id }}>
							<Button variant="secondary" size="sm">
								{t("createOrder")}
							</Button>
						</Link>
						<Link to="/contracts/new" search={{ companyId: id }}>
							<Button variant="secondary" size="sm">
								{t("createContract")}
							</Button>
						</Link>
						<Link to="/activities" search={{ companyId: id } as never}>
							<Button variant="secondary" size="sm">
								{t("createActivity")}
							</Button>
						</Link>
						<Button variant="secondary" onClick={openEdit}>
							{t("edit")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("companyInfo")}>
					<div className={styles.infoGrid}>
						<Info label={t("tradeName")} value={company.tradeName} />
						<Info label={t("taxNumber")} value={company.taxNumber} />
						<Info label={t("taxOffice")} value={company.taxOffice} />
						<Info label={t("phone")} value={company.phone} />
						<Info label={t("website")} value={company.website} />
						<Info label={t("industry")} value={company.industry} />
						<Info label={t("sapCardCode")} value={company.sapCardCode} />
						<Info
							label={t("balance")}
							value={company.balance != null ? String(company.balance) : "-"}
						/>
						<Info label={t("currency")} value={company.currency} />
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge variant="primary">{t(company.status)}</Badge>
						</div>
					</div>
				</Card>

				<Card
					header={
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<span>{t("contactPersons")}</span>
							<Button
								size="sm"
								variant="secondary"
								onClick={() => {
									setPersonData({});
									setPersonError("");
									setPersonOpen(true);
								}}
							>
								{t("add")}
							</Button>
						</div>
					}
				>
					{personsQuery.isLoading && <LoadingSpinner size="sm" />}
					{(personsQuery.data?.items ?? []).length === 0 && (
						<p className={styles.empty}>{t("noData")}</p>
					)}
					{(personsQuery.data?.items ?? []).map((p) => (
						<div key={p.id} className={styles.listItem}>
							<p className={styles.itemTitle}>
								{p.firstName} {p.lastName}
								{p.isPrimary === "true" && <Badge variant="primary">{t("primary")}</Badge>}
							</p>
							<p className={styles.itemMeta}>
								{p.title}
								{p.recordType ? ` · ${p.recordType}` : ""}
								{p.sealInfo ? ` · ${p.sealInfo}` : ""} · {p.email} · {p.phone}
							</p>
						</div>
					))}
				</Card>

				<Card header={t("linkedOffers")}>
					{(offersQuery.data?.items ?? []).length === 0 && (
						<p className={styles.empty}>{t("noData")}</p>
					)}
					{(offersQuery.data?.items ?? []).map((o) => (
						<div key={o.id} className={styles.listItem}>
							<p className={styles.itemTitle}>{o.documentNumber || o.id.slice(0, 8)}</p>
							<p className={styles.itemMeta}>
								{formatAmount(o.total)} {o.currency}
							</p>
						</div>
					))}
				</Card>

				<Card header={t("activityTimeline")}>
					{(activitiesQuery.data?.items ?? []).length === 0 && (
						<p className={styles.empty}>{t("noData")}</p>
					)}
					{(activitiesQuery.data?.items ?? []).map((a) => (
						<div key={a.id} className={styles.listItem}>
							<p className={styles.itemTitle}>{a.subject}</p>
							<p className={styles.itemMeta}>
								{a.type} · {a.dueDate ? formatDate(a.dueDate) : "-"}
							</p>
						</div>
					))}
				</Card>
			</div>

			<Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title={t("edit")}>
				{editError && <div className={styles.formError}>{editError}</div>}
				<FormField label={t("legalName")} required>
					<Input
						value={editData.legalName ?? ""}
						onChange={(e) => setEditData({ ...editData, legalName: e.target.value })}
						error={fieldErrors.legalName}
					/>
				</FormField>
				<FormField label={t("tradeName")}>
					<Input
						value={editData.tradeName ?? ""}
						onChange={(e) => setEditData({ ...editData, tradeName: e.target.value })}
					/>
				</FormField>
				<FormField label={t("taxNumber")}>
					<Input
						value={editData.taxNumber ?? ""}
						onChange={(e) => setEditData({ ...editData, taxNumber: e.target.value })}
					/>
				</FormField>
				<FormField label={t("taxOffice")}>
					<Input
						value={editData.taxOffice ?? ""}
						onChange={(e) => setEditData({ ...editData, taxOffice: e.target.value })}
					/>
				</FormField>
				<FormField label={t("phone")}>
					<Input
						value={editData.phone ?? ""}
						onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
					/>
				</FormField>
				<FormField label={t("website")}>
					<Input
						value={editData.website ?? ""}
						onChange={(e) => setEditData({ ...editData, website: e.target.value })}
					/>
				</FormField>
				<FormField label={t("industry")}>
					<Input
						value={editData.industry ?? ""}
						onChange={(e) => setEditData({ ...editData, industry: e.target.value })}
					/>
				</FormField>
				<FormField label={t("balance")}>
					<Input
						type="number"
						value={editData.balance ?? ""}
						onChange={(e) => setEditData({ ...editData, balance: e.target.value })}
					/>
				</FormField>
				<FormField label={t("currency")}>
					<Input
						value={editData.currency ?? ""}
						onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
					/>
				</FormField>
				<FormField label={t("status")}>
					<Select
						options={[
							{ value: "lead", label: t("lead") },
							{ value: "customer", label: t("customer") },
							{ value: "vendor", label: t("vendor") },
							{ value: "inactive", label: t("inactive") },
						]}
						value={editData.status ?? company.status}
						onChange={(e) => setEditData({ ...editData, status: e.target.value })}
					/>
				</FormField>
				<FormField label="Adres">
					<Input
						value={editData.addressStreet ?? ""}
						onChange={(e) => setEditData({ ...editData, addressStreet: e.target.value })}
					/>
				</FormField>
				<FormField label="Posta Kodu">
					<Input
						value={editData.addressPostalCode ?? ""}
						onChange={(e) => setEditData({ ...editData, addressPostalCode: e.target.value })}
					/>
				</FormField>
				<FormField label="İlçe">
					<Input
						value={editData.addressCity ?? ""}
						onChange={(e) => setEditData({ ...editData, addressCity: e.target.value })}
					/>
				</FormField>
				<FormField label="İl">
					<Select
						value={editData.addressState ?? ""}
						onChange={(e) => setEditData({ ...editData, addressState: e.target.value })}
						options={[
							{ value: "", label: t("notAvailable") },
							...(states ?? []).map((s) => ({ value: s.name, label: s.name })),
						]}
					/>
				</FormField>
				<FormField label="Ülke">
					<Select
						value={editData.addressCountry ?? ""}
						onChange={(e) =>
							setEditData({ ...editData, addressCountry: e.target.value, addressState: "" })
						}
						options={[
							{ value: "", label: t("notAvailable") },
							...(countries ?? []).map((c) => ({ value: c.name, label: c.name })),
						]}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setEditOpen(false)}>
						{t("cancel")}
					</Button>
					<Button onClick={saveEdit} isLoading={updateCompany.isPending}>
						{t("save")}
					</Button>
				</div>
			</Modal>

			<Modal isOpen={personOpen} onClose={() => setPersonOpen(false)} title={t("addContactPerson")}>
				{personError && <div className={styles.formError}>{personError}</div>}
				<FormField label={t("firstName")} required>
					<Input
						value={personData.firstName ?? ""}
						onChange={(e) => setPersonData({ ...personData, firstName: e.target.value })}
					/>
				</FormField>
				<FormField label={t("lastName")} required>
					<Input
						value={personData.lastName ?? ""}
						onChange={(e) => setPersonData({ ...personData, lastName: e.target.value })}
					/>
				</FormField>
				<FormField label={t("title")}>
					<Input
						value={personData.title ?? ""}
						onChange={(e) => setPersonData({ ...personData, title: e.target.value })}
					/>
				</FormField>
				<FormField label={t("phone")}>
					<Input
						value={personData.phone ?? ""}
						onChange={(e) => setPersonData({ ...personData, phone: e.target.value })}
					/>
				</FormField>
				<FormField label={t("mobile")}>
					<Input
						value={personData.mobile ?? ""}
						onChange={(e) => setPersonData({ ...personData, mobile: e.target.value })}
					/>
				</FormField>
				<FormField label={t("email")}>
					<Input
						value={personData.email ?? ""}
						onChange={(e) => setPersonData({ ...personData, email: e.target.value })}
					/>
				</FormField>
				<FormField label={t("notes")}>
					<Input
						value={personData.notes ?? ""}
						onChange={(e) => setPersonData({ ...personData, notes: e.target.value })}
					/>
				</FormField>
				<div className={styles.modalActions}>
					<Button variant="ghost" onClick={() => setPersonOpen(false)}>
						{t("cancel")}
					</Button>
					<Button
						onClick={() => {
							setPersonError("");
							if (!personData.firstName?.trim()) {
								setPersonError(t("requiredField"));
								return;
							}
							if (!personData.lastName?.trim()) {
								setPersonError(t("requiredField"));
								return;
							}
							createPerson.mutate({ companyId: id, ...personData } as never, {
								onSuccess: () => setPersonOpen(false),
								onError: (err) =>
									setPersonError(err instanceof Error ? err.message : t("updateFailed")),
							});
						}}
						isLoading={createPerson.isPending}
					>
						{t("save")}
					</Button>
				</div>
			</Modal>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
